import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useNotification } from '../contexts/NotificationContext';
import UpgradeModal from './UpgradeModal';

type WorkspaceSelectionMode = 'campaign' | 'automation' | 'segment';

interface WorkspaceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkspaceSelected: (workspaceId: string) => void;
  mode?: WorkspaceSelectionMode;
}

const MODAL_COPY: Record<WorkspaceSelectionMode, { title: string; description: string; primaryAction: string; loadingAction: string }> = {
  campaign: {
    title: 'Create New Campaign',
    description: 'Choose a workspace to create your campaign in',
    primaryAction: 'Continue',
    loadingAction: 'Creating...'
  },
  automation: {
    title: 'Launch Automation',
    description: 'Select the workspace where this automation should run',
    primaryAction: 'Launch',
    loadingAction: 'Launching...'
  },
  segment: {
    title: 'Create Segment',
    description: 'Pick a workspace to organize and target subscribers',
    primaryAction: 'Create Segment',
    loadingAction: 'Creating...'
  }
};

const WorkspaceSelectionModal: React.FC<WorkspaceSelectionModalProps> = ({ isOpen, onClose, onWorkspaceSelected, mode = 'campaign' }) => {
  const { workspaces, createWorkspace } = useWorkspace();
  const { showError } = useNotification();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [userPlan, setUserPlan] = useState<string>('free');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Load user plan
  React.useEffect(() => {
    const loadPlan = async () => {
      const { planService } = await import('../services/planService');
      const { auth } = await import('../firebase');
      const userId = auth.currentUser?.uid;
      if (userId) {
        const plan = await planService.getUserPlan(userId);
        setUserPlan(plan);
      }
    };
    loadPlan();
  }, []);

  const handleClose = () => {
    onClose();
  };

  const handleConfirmSelection = async () => {
    if (selectedWorkspaceId) {
      setIsCreating(true);
      try {
        await onWorkspaceSelected(selectedWorkspaceId);
        onClose();
      } catch (error) {
        console.error('Error creating campaign after workspace selection:', error);
      } finally {
        setIsCreating(false);
      }
    }
  };

  const handleCreateWorkspace = async () => {
    if (newWorkspaceName.trim()) {
      setIsCreatingWorkspace(true);
      try {
        // Pre-check workspace limits
        const { planService } = await import('../services/planService');
        const { auth } = await import('../firebase');
        const userId = auth.currentUser?.uid;
        if (userId) {
          const limitCheck = await planService.canCreateWorkspace(userId);
          if (!limitCheck.allowed) {
            setShowUpgradeModal(true);
            setIsCreatingWorkspace(false);
            return;
          }
        }

        const workspaceData = {
          name: newWorkspaceName.trim(),
          description: '',
          primaryColor: '#805AD5',
          secondaryColor: '#6B46C1',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          currency: 'USD',
          language: 'en'
        };
        const newWorkspaceId = await createWorkspace(workspaceData);
        setSelectedWorkspaceId(newWorkspaceId);
        setShowCreateWorkspace(false);
        setNewWorkspaceName('');
      } catch (error: any) {
        console.error('Error creating workspace:', error);
        showError(error.message || 'Failed to create workspace. Please check your plan limits.');
      } finally {
        setIsCreatingWorkspace(false);
      }
    }
  };

  if (!isOpen) return null;

  const { title, description, primaryAction, loadingAction } = MODAL_COPY[mode];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4" style={{ fontFamily: 'Arial, sans-serif' }}>
      <div className="relative w-full max-w-lg bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden" style={{ fontFamily: 'Arial, sans-serif' }}>
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {title}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {description}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-full transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Workspace Selection */}
          <div className="mb-6">
            {/* Scrollable Workspace List */}
            <div className="max-h-80 overflow-y-auto space-y-2 pr-2">
              {workspaces.map((workspace, index) => {
                const isLocked = (userPlan === 'starter' || userPlan === 'free') && index > 0;

                return (
                  <div
                    key={workspace.id}
                    className={`group relative p-4 border rounded-lg transition-all duration-200 ${isLocked
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : selectedWorkspaceId === workspace.id
                        ? 'border-black bg-gray-50 shadow-sm ring-2 ring-gray-200 cursor-pointer'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                      }`}
                    onClick={() => {
                      if (!isLocked) {
                        setSelectedWorkspaceId(workspace.id);
                      }
                    }}
                  >
                    {isLocked && (
                      <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[10px] font-bold uppercase tracking-wider z-10 shadow-sm">
                        <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 3.3L19 6.41V11C19 15.47 16.05 19.45 12 20.93C7.95 19.45 5 15.47 5 11V6.41L12 3.3ZM12 7C10.34 7 9 8.34 9 10V12H8V17H16V12H15V10C15 8.34 13.66 7 12 7ZM12 8.5C12.83 8.5 13.5 9.17 13.5 10V12H10.5V10C10.5 9.17 11.17 8.5 12 8.5Z" />
                        </svg>
                        Locked
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${selectedWorkspaceId === workspace.id
                          ? 'border-black bg-black'
                          : 'border-gray-300 group-hover:border-gray-400'
                          }`}>
                          {selectedWorkspaceId === workspace.id && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{workspace.name}</div>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className={`w-2 h-2 rounded-full transition-colors duration-200 ${selectedWorkspaceId === workspace.id ? 'bg-black' : 'bg-gray-300'
                          }`} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Create New Workspace Option - Outside Scroll Area */}
            <div className="mt-4">
              {!showCreateWorkspace ? (
                <div
                  className={`group relative p-4 border-2 border-dashed rounded-lg transition-all duration-200 ${userPlan === 'starter' || userPlan === 'free'
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : 'border-gray-300 cursor-pointer hover:border-black hover:bg-gray-50'
                    }`}
                  onClick={() => {
                    if (userPlan === 'starter' || userPlan === 'free') {
                      setShowUpgradeModal(true);
                    } else {
                      setShowCreateWorkspace(true);
                    }
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full border-2 border-dashed flex items-center justify-center ${userPlan === 'starter' || userPlan === 'free' ? 'border-gray-300' : 'border-gray-400'}`}>
                      <svg className={`w-2.5 h-2.5 ${userPlan === 'starter' || userPlan === 'free' ? 'text-gray-300' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm font-semibold ${userPlan === 'starter' || userPlan === 'free' ? 'text-gray-400' : 'text-gray-600 group-hover:text-black'}`}>
                        Create New Workspace
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 border border-gray-200 bg-gray-50 rounded-lg">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Workspace Name
                      </label>
                      <input
                        type="text"
                        value={newWorkspaceName}
                        onChange={(e) => setNewWorkspaceName(e.target.value)}
                        placeholder="Enter workspace name"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        autoFocus
                        onKeyPress={(e) => e.key === 'Enter' && handleCreateWorkspace()}
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCreateWorkspace}
                        disabled={!newWorkspaceName.trim() || isCreatingWorkspace}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isCreatingWorkspace ? 'Creating...' : 'Create'}
                      </button>
                      <button
                        onClick={() => {
                          setShowCreateWorkspace(false);
                          setNewWorkspaceName('');
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmSelection}
              disabled={!selectedWorkspaceId || isCreating}
              className="px-6 py-2 text-sm font-medium text-white bg-black border border-transparent rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
            >
              {isCreating ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {loadingAction}
                </div>
              ) : (
                primaryAction
              )}
            </button>
          </div>
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSelectionModal;
